import type { QueryClient } from "@tanstack/react-query";

import { automationsKeys } from "./automationsQueryKeys";

export type AutomationsInvalidationScope =
  | "entitlement"
  | "billing"
  | "usage"
  | "creditBalance";

const ALL_SCOPES: AutomationsInvalidationScope[] = [
  "entitlement",
  "billing",
  "usage",
  "creditBalance",
];

/**
 * Refetch the Automations queries a mutation touched. Callers that already
 * wrote the entitlement into the cache omit `"entitlement"`. Checkout-session
 * and change-preview queries are never matched.
 */
export async function invalidateAutomations(
  queryClient: QueryClient,
  {
    artistId,
    scope = ALL_SCOPES,
  }: { artistId: string; scope?: AutomationsInvalidationScope[] }
): Promise<void> {
  const keyFor: Record<
    AutomationsInvalidationScope,
    readonly [string, ...string[]]
  > = {
    entitlement: automationsKeys.entitlement(artistId),
    billing: automationsKeys.billing(artistId),
    usage: automationsKeys.usageAll(artistId),
    creditBalance: automationsKeys.creditBalance(artistId),
  };
  await Promise.all(
    scope.map((s) => queryClient.invalidateQueries({ queryKey: keyFor[s] }))
  );
}

/** A paid checkout session is single-use; drop it so it is never re-served from cache. */
export function removeAutomationsCheckoutSessions(
  queryClient: QueryClient,
  artistId: string
): void {
  queryClient.removeQueries({
    queryKey: automationsKeys.checkoutSessionsAll(artistId),
  });
}
